import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";

const API_URL = "http://localhost:5000/api";

function App() {
  const [ideas, setIdeas] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch ideas on load
  useEffect(() => {
    console.log(" Frontend: Fetching ideas from backend...");
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    try {
      console.log(` Fetching: ${API_URL}/ideas`);
      const response = await axios.get(`${API_URL}/ideas`);
      console.log(" Received ideas:", response.data);
      
      setIdeas(Array.isArray(response.data) ? response.data : []);
      setError("");
    } catch (err) {
      console.error(" Fetch error:", err);
      setError("Failed to load ideas. Make sure backend is running on port 5000.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!content.trim()) {
      setError("Content is required");
      return;
    }

    const newIdea = {
      title: title.trim(),
      content: content.trim()
    };

    console.log(" Sending POST request:", newIdea);
    
    try {
      const response = await axios.post(`${API_URL}/ideas`, newIdea);
      console.log(" POST response:", response.data);
      
      setIdeas([response.data, ...ideas]);
      setTitle("");
      setContent("");
      setSuccessMessage("Idea added successfully!");
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error(" POST error:", err);
      console.error("Error details:", err.response?.data);
      setError(err.response?.data?.error || "Failed to add idea. Check console.");
    }
  };

  const updateStatus = async (id, status) => {
    console.log(` Updating idea ${id} status to: ${status}`);
    
    try {
      const response = await axios.put(`${API_URL}/ideas/${id}/status`, { status });
      console.log(" Status update response:", response.data);
      
      setIdeas(ideas.map(idea => idea.id === id ? response.data : idea));
    } catch (err) {
      console.error(" Status update error:", err);
      setError(err.response?.data?.error || "Failed to update status");
    }
  };

  const deleteIdea = async (id) => {
    if (!window.confirm("Are you sure you want to delete this idea?")) {
      return;
    }
    
    console.log(` Deleting idea ${id}`);
    
    try {
      await axios.delete(`${API_URL}/ideas/${id}`);
      console.log(" Deleted idea", id);
      
      setIdeas(ideas.filter(idea => idea.id !== id));
    } catch (err) {
      console.error(" Delete error:", err);
      setError(err.response?.data?.error || "Failed to delete idea");
    }
  };

  const testConnection = async () => {
    console.log(" Testing API connection...");
    
    try {
      const health = await axios.get(`${API_URL}/health`);
      console.log(" Health check:", health.data);
      
      const ideasRes = await axios.get(`${API_URL}/ideas`);
      console.log(` Current ideas: ${ideasRes.data.length} items`);
      
      alert(` Connection successful!\nBackend: ${health.data.status}\nIdeas: ${ideasRes.data.length}`);
    } catch (err) {
      console.error(" Connection test failed:", err);
      alert(" Connection failed: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading your ideas...</p>
        <button onClick={fetchIdeas} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <h1> Content Scratchpad</h1>
        <p>Track A: Content Scratchpad + Planning Queue</p>
        <button onClick={testConnection} className="test-btn">
          Test Connection
        </button>
      </header>

      <main className="main">
        {/* LEFT PANEL - Add Idea Form */}
        <div className="left-panel">
          <h2>Add New Idea</h2>
          
          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter idea title"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="content">Content *</label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Describe your idea..."
                rows="5"
              />
            </div>
            
            <button type="submit" className="submit-btn">
              Add Idea
            </button>
          </form>
          
          <div className="api-info">
            <p><strong>Backend Status:</strong> <span className="status-online">Online ✓</span></p>
            <p><strong>API URL:</strong> <code>{API_URL}</code></p>
            <p><strong>Total Ideas:</strong> {ideas.length}</p>
            <button onClick={fetchIdeas} className="refresh-small-btn">
              Refresh List
            </button>
          </div>
        </div>

        {/* RIGHT PANEL - Idea List */}
        <div className="right-panel">
          <div className="panel-header">
            <h2>Your Ideas ({ideas.length})</h2>
            <button 
              onClick={fetchIdeas}
              className="refresh-btn"
              title="Refresh ideas"
            >
               Refresh
            </button>
          </div>
          
          {ideas.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"></div>
              <h3>No ideas yet</h3>
              <p>Start by adding your first idea using the form on the left!</p>
              <div className="status-example">
                <p><strong>Status Flow:</strong></p>
                <div className="status-flow">
                  <span className="status-draft">Draft</span>
                  <span>→</span>
                  <span className="status-scheduled">Scheduled</span>
                  <span>→</span>
                  <span className="status-published">Published</span>
                </div>
                <p className="note">Click on status badges to move ideas through workflow</p>
              </div>
            </div>
          ) : (
            <div className="ideas-container">
              {ideas.map(idea => (
                <div key={idea.id} className="idea-card">
                  <div className="idea-header">
                    <h3>{idea.title}</h3>
                    <button 
                      onClick={() => deleteIdea(idea.id)}
                      className="delete-btn"
                      title="Delete idea"
                    >
                      ×
                    </button>
                  </div>
                  
                  <p className="idea-content">{idea.content}</p>
                  
                  <div className="idea-footer">
                    <button 
                      onClick={() => {
                        const nextStatus = 
                          idea.status === "draft" ? "scheduled" :
                          idea.status === "scheduled" ? "published" : "draft";
                        updateStatus(idea.id, nextStatus);
                      }}
                      className="status-badge"
                      style={{
                        backgroundColor: 
                          idea.status === "draft" ? "#ff9800" :
                          idea.status === "scheduled" ? "#2196f3" : "#4caf50"
                      }}
                    >
                      {idea.status.charAt(0).toUpperCase() + idea.status.slice(1)}
                    </button>
                    
                    <div className="idea-meta">
                      <span className="idea-date">
                        {new Date(idea.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      <span className="idea-id">ID: {idea.id}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="footer">
        <p>CS50x Final Project - Track A: Content Scratchpad + Planning Queue</p>
        <p className="footer-note">
          Backend: <code>http://localhost:5000</code> | 
          Frontend: <code>http://localhost:3000</code> | 
          Open browser console (F12) for debugging
        </p>
      </footer>
    </div>
  );
}

export default App;